import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';

const API = process.env.REACT_APP_API_URL || '/api';

const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

export default function ScheduleSettings() {
  const { t } = useLanguage();
  const [hours, setHours] = useState([]);
  const [closures, setClosures] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [newClosure, setNewClosure] = useState({ start_date: '', end_date: '', reason: '' });

  useEffect(() => {
    loadHours();
    loadClosures();
  }, []);

  const loadHours = async () => {
    try {
      const res = await axios.get(`${API}/schedule/hours`);
      const normalized = res.data.map(h => ({
        ...h,
        open_time: h.open_time || (h.is_closed ? null : '08:00'),
        close_time: h.close_time || (h.is_closed ? null : '18:00'),
      }));
      setHours(normalized);
    } catch (err) { /* ignore */ }
  };

  const loadClosures = async () => {
    try {
      const res = await axios.get(`${API}/schedule/closures`);
      setClosures(res.data);
    } catch (err) { /* ignore */ }
  };

  const saveHours = async () => {
    setSaving(true);
    setMsg({ type: '', text: '' });
    try {
      await axios.put(`${API}/schedule/hours`, { hours });
      setMsg({ type: 'success', text: t.admin.schedule?.saveSuccess || 'Schedule saved successfully' });
      loadHours();
    } catch (err) {
      setMsg({ type: 'danger', text: err.response?.data?.error || t.admin.schedule?.saveError || 'Failed to save schedule' });
    } finally {
      setSaving(false);
    }
  };

  const addClosure = async () => {
    if (!newClosure.start_date || !newClosure.end_date) return;
    try {
      await axios.post(`${API}/schedule/closures`, newClosure);
      setNewClosure({ start_date: '', end_date: '', reason: '' });
      loadClosures();
    } catch (err) {
      setMsg({ type: 'danger', text: err.response?.data?.error || 'Failed to add closure' });
    }
  };

  const deleteClosure = async (id) => {
    try {
      await axios.delete(`${API}/schedule/closures/${id}`);
      loadClosures();
    } catch (err) { /* ignore */ }
  };

  const updateHour = (dayOfWeek, field, value) => {
    setHours(prev => prev.map(h =>
      h.day_of_week === dayOfWeek ? { ...h, [field]: value } : h
    ));
  };

  const getDayLabel = (dow) => {
    const key = DAY_KEYS[dow];
    return t.business?.hours?.[key] || key;
  };

  return (
    <div>
      {msg.text && (
        <div className={`alert alert-${msg.type}`} role="alert">{msg.text}</div>
      )}

      {/* Weekly Hours */}
      <h6 style={{ color: '#D42B2B', fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '1rem' }}>
        {t.admin.schedule?.weeklyHours || 'Weekly Hours'}
      </h6>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
        {hours.map(h => (
          <div key={h.day_of_week} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0', borderBottom: '1px solid rgba(148,163,184,0.1)' }}>
            <div style={{ width: '100px', color: '#E8ECF0', fontSize: '0.85rem' }}>{getDayLabel(h.day_of_week)}</div>
            <div className="form-check form-switch" style={{ minWidth: '80px' }}>
              <input
                className="form-check-input"
                type="checkbox"
                checked={!h.is_closed}
                onChange={e => updateHour(h.day_of_week, 'is_closed', !e.target.checked)}
              />
              <label className="form-check-label" style={{ fontSize: '0.75rem', color: h.is_closed ? '#d07070' : '#70d070' }}>
                {h.is_closed ? (t.business?.hours?.closed || 'Closed') : (t.admin.schedule?.open || 'Open')}
              </label>
            </div>
            {!h.is_closed && (
              <>
                <input
                  type="time"
                  className="form-control form-control-sm"
                  style={{ width: '120px' }}
                  value={h.open_time?.substring(0, 5) || '08:00'}
                  onChange={e => updateHour(h.day_of_week, 'open_time', e.target.value)}
                />
                <span style={{ color: '#94A3B8', fontSize: '0.8rem' }}>—</span>
                <input
                  type="time"
                  className="form-control form-control-sm"
                  style={{ width: '120px' }}
                  value={h.close_time?.substring(0, 5) || '18:00'}
                  onChange={e => updateHour(h.day_of_week, 'close_time', e.target.value)}
                />
              </>
            )}
          </div>
        ))}
      </div>

      <button className="btn btn-primary btn-sm mb-4" onClick={saveHours} disabled={saving}>
        {saving ? (t.admin.settings?.saving || 'Saving...') : (t.admin.schedule?.saveHours || 'Save Hours')}
      </button>

      {/* Closures */}
      <h6 style={{ color: '#D42B2B', fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '1rem' }}>
        {t.admin.schedule?.closures || 'Scheduled Closures'}
      </h6>

      {closures.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          {closures.map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid rgba(148,163,184,0.1)' }}>
              <div>
                <span style={{ color: '#E8ECF0', fontSize: '0.85rem' }}>{c.start_date} — {c.end_date}</span>
                {c.reason && <span style={{ color: '#94A3B8', fontSize: '0.8rem', marginLeft: '0.5rem' }}>({c.reason})</span>}
              </div>
              <button className="btn btn-outline-danger btn-sm" onClick={() => deleteClosure(c.id)} style={{ fontSize: '0.7rem' }}>
                {t.common?.delete || 'Delete'}
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="row g-2 align-items-end">
        <div className="col-auto">
          <label className="form-label" style={{ fontSize: '0.7rem' }}>{t.admin.schedule?.startDate || 'Start Date'}</label>
          <input type="date" className="form-control form-control-sm" value={newClosure.start_date}
            onChange={e => setNewClosure({ ...newClosure, start_date: e.target.value })} style={{ colorScheme: 'dark' }} />
        </div>
        <div className="col-auto">
          <label className="form-label" style={{ fontSize: '0.7rem' }}>{t.admin.schedule?.endDate || 'End Date'}</label>
          <input type="date" className="form-control form-control-sm" value={newClosure.end_date}
            onChange={e => setNewClosure({ ...newClosure, end_date: e.target.value })} style={{ colorScheme: 'dark' }} />
        </div>
        <div className="col">
          <label className="form-label" style={{ fontSize: '0.7rem' }}>{t.admin.schedule?.reason || 'Reason'}</label>
          <input type="text" className="form-control form-control-sm" value={newClosure.reason}
            onChange={e => setNewClosure({ ...newClosure, reason: e.target.value })}
            placeholder={t.admin.schedule?.reasonPlaceholder || 'Holiday, vacation...'} />
        </div>
        <div className="col-auto">
          <button className="btn btn-primary btn-sm" onClick={addClosure} disabled={!newClosure.start_date || !newClosure.end_date}>
            {t.admin.schedule?.addClosure || 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}
